import { StandardEvents } from '@shopify/events';

let pendingCartRequest = null;

const TRUCK_ICON = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 7h11v9H3z"/>
    <path d="M14 10h3l4 4v2h-7z"/>
    <circle cx="7" cy="18" r="2"/>
    <circle cx="18" cy="18" r="2"/>
    <path d="M5 4h6"/>
  </svg>
`;

const CHECK_ICON = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    <path d="m5 12 4 4L19 6"/>
  </svg>
`;

function fetchLiveCart() {
  if (pendingCartRequest) return pendingCartRequest;

  const cartUrl = window.Theme?.routes?.cart_url || '/cart';
  pendingCartRequest = fetch(`${cartUrl}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to refresh free shipping status: ${response.status}`);
      }

      return response.json();
    })
    .finally(() => {
      pendingCartRequest = null;
    });

  return pendingCartRequest;
}

class NmvxFreeShipping extends HTMLElement {
  #abortController = null;
  #refreshToken = 0;

  connectedCallback() {
    if (this.dataset.mode !== 'product') return;

    this.#bindCartEvents();
    queueMicrotask(() => this.#refreshFromCart());
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #bindCartEvents() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
  }

  #handleCartUpdate = (event) => {
    const completion = event?.promise;

    if (completion && typeof completion.then === 'function') {
      completion
        .then(({ detail } = {}) => {
          if (detail?.didError) return;
          this.#refreshFromCart();
        })
        .catch(() => {
          // The theme displays cart errors; preserve the last confirmed state.
        });
      return;
    }

    window.setTimeout(() => this.#refreshFromCart(), 350);
  };

  #handlePageShow = () => {
    this.#refreshFromCart();
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#refreshFromCart();
    }
  };

  async #refreshFromCart() {
    const refreshToken = ++this.#refreshToken;

    try {
      const cart = await fetchLiveCart();
      if (!this.isConnected || refreshToken !== this.#refreshToken) return;
      this.#renderCartState(cart);
    } catch (error) {
      console.warn('[NAMVEXO free shipping] Unable to refresh cart status.', error);
    }
  }

  #renderCartState(cart) {
    const threshold = Number(this.dataset.thresholdCents || 5000);
    const total = Number(cart?.total_price || 0);
    const itemCount = Number(cart?.item_count || 0);
    const remaining = Math.max(threshold - total, 0);
    const progress = threshold > 0 ? Math.min((total / threshold) * 100, 100) : 100;
    const unlocked = total >= threshold;
    const thresholdDisplay = this.#formatMoney(threshold);
    const remainingDisplay = this.#formatMoney(remaining);

    const icon = this.querySelector('[data-nmvx-shipping-icon]');
    const headline = this.querySelector('[data-nmvx-shipping-headline]');
    const detail = this.querySelector('[data-nmvx-shipping-detail]');
    const progressTrack = this.querySelector('[data-nmvx-shipping-progress]');
    const progressFill = this.querySelector('[data-nmvx-shipping-progress-fill]');
    const meta = this.querySelector('[data-nmvx-shipping-meta]');
    const totalLabel = this.querySelector('[data-nmvx-shipping-total]');
    const secondaryLabel = this.querySelector('[data-nmvx-shipping-secondary-label]');
    const remainingLabel = this.querySelector('[data-nmvx-shipping-remaining]');

    this.classList.toggle('is-unlocked', unlocked);
    this.classList.toggle('is-empty', itemCount === 0);

    if (icon) icon.innerHTML = unlocked ? CHECK_ICON : TRUCK_ICON;

    if (unlocked) {
      if (headline) headline.textContent = 'Free shipping unlocked.';
      if (detail) detail.textContent = 'Standard shipping is included with this order.';
    } else if (itemCount > 0) {
      if (headline) headline.textContent = `You’re ${remainingDisplay} away from free shipping.`;
      if (detail) detail.textContent = `Orders of ${thresholdDisplay} or more receive free standard shipping.`;
    } else {
      if (headline) headline.textContent = `Free shipping on orders ${thresholdDisplay}+.`;
      if (detail) detail.textContent = 'Build your order to the threshold and standard shipping is included.';
    }

    const showProgress = itemCount > 0;
    if (progressTrack) {
      progressTrack.hidden = !showProgress;
      progressTrack.setAttribute('aria-valuenow', progress.toFixed(1));
      progressTrack.setAttribute(
        'aria-valuetext',
        unlocked ? 'Free shipping unlocked' : `${remainingDisplay} remaining to free shipping`
      );
    }

    if (progressFill) {
      progressFill.style.setProperty('--nmvx-shipping-progress', `${progress.toFixed(1)}%`);
    }

    if (meta) meta.hidden = !showProgress;
    if (totalLabel) totalLabel.textContent = this.#formatMoney(total);
    if (secondaryLabel) secondaryLabel.textContent = unlocked ? 'Shipping' : 'Remaining';
    if (remainingLabel) remainingLabel.textContent = unlocked ? 'Unlocked' : remainingDisplay;
  }

  #formatMoney(cents) {
    const currency = this.dataset.currency || 'USD';
    const locale = this.dataset.locale || document.documentElement.lang || 'en';
    const showCurrency = this.dataset.showCurrency === 'true';
    const amount = Number(cents || 0) / 100;

    try {
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);

      return showCurrency ? `${formatted} ${currency}` : formatted;
    } catch (_error) {
      const formatted = `$${amount.toFixed(2)}`;
      return showCurrency ? `${formatted} ${currency}` : formatted;
    }
  }
}

if (!customElements.get('nmvx-free-shipping')) {
  customElements.define('nmvx-free-shipping', NmvxFreeShipping);
}